import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import type { StatusColor } from "../../components/UIToolkit/Badges/StatusDot";
import { searchNeedles } from "../../components/UIToolkit/Inputs/SearchTerms/searchTerms";
import { categoricalColorAt, freeLabelColor } from "../../utils/categoricalColors";
import { matchingWorkHistoryEntry } from "./candidate/workHistory";
import { END_OUTCOME_LABEL_KEY, END_REASON_LABEL_KEY, END_REASON_NOT_GIVEN } from "./consts";
import { boardColumns, stepNameKey } from "./pipeline/boardColumns";
import type {
	CareersApplicationEnd,
	CareersApplicationRow,
	CareersApplicationStatus,
	CareersCandidate,
	CareersEndChoice,
	CareersEndOutcome,
	CareersEndReason,
	CareersEndReasonNotGiven,
	CareersJob,
	CareersStaleness,
	CareersWorkHistoryEntry,
} from "./types";

/**
 * Pure presentation helpers for Careers. No React, no i18n instance: label helpers return an i18n KEY plus
 * its params, so the rule is testable on its own and the component still owns the translation.
 */

export type TranslatableLabel = { key: string; params: Record<string, string | number> };

/** Staleness → the `StatusDot` colour name. Hired / ended carry no staleness and read neutral. */
export const stalenessDotColor = (staleness: CareersStaleness | null): StatusColor => {
	switch (staleness) {
		case "fresh":
			return "green";
		case "aging":
			return "yellow";
		case "stale":
			return "red";
		case null:
			return "gray";
	}
};

/** Staleness → a theme palette key for a card's accent rail, or null (no rail) when there is none. */
export const stalenessPaletteKey = (staleness: CareersStaleness | null): "success" | "warning" | "error" | null => {
	switch (staleness) {
		case "fresh":
			return "success";
		case "aging":
			return "warning";
		case "stale":
			return "error";
		case null:
			return null;
	}
};

/** An all-caps word of 2+ letters is an acronym ("DON", "RN", "CNA"); lowercasing it would misspell it. */
const ACRONYM = /^\p{Lu}{2,}$/u;

/**
 * A step name set mid-sentence ("0d · in phone screen"): lowercased, acronyms kept as written. Tested per run of
 * letters, not per space-separated word, so an acronym touching punctuation ("RN, interview", "RN/Review") is
 * still one.
 */
export const stepNameInSentence = (name: string): string => name.replace(/\p{L}+/gu, (run) => (ACRONYM.test(run) ? run : run.toLocaleLowerCase()));

/**
 * The line under an IN-PROCESS candidate on the board — "5d · in phone screen". The column already names
 * the step, but the line reads on its own in the drag overlay. Hired and ended applications have no
 * such line: they are not waiting on a step, so the card shows their status chip instead.
 */
export const stalenessLabel = (row: Pick<CareersApplicationRow, "days_in_step" | "current_step_name">): TranslatableLabel => ({
	key: "careers.card.in_step",
	params: { days: row.days_in_step, step: stepNameInSentence(row.current_step_name) },
});

/**
 * Every application but an ended one can change step (the backend 409s that one: reopen it first). A hired
 * one moves too: out of the hired step is how a hire is undone, and the backend clears `hired_at` when it leaves.
 */
export const canChangeStep = (row: Pick<CareersApplicationRow, "status">): boolean => row.status !== "ended";

/**
 * "Declined offer · Salary" (or just "Declined offer" when no reason was given) - how a process ended, the one
 * wording the board card, the table, the profile and the history share. Takes `t` from the caller (this module
 * has no i18n instance of its own).
 */
export const endSummaryText = (t: TFunction, end: CareersEndChoice): string => {
	const outcome = t(END_OUTCOME_LABEL_KEY[end.outcome]);
	return end.reason === END_REASON_NOT_GIVEN ? outcome : t("careers.end.summary", { outcome, reason: t(END_REASON_LABEL_KEY[end.reason]) });
};

/** "Ended at Offer: Declined offer · Salary" plus the note when there is one - the hover on an end chip. */
export const endDetailText = (t: TFunction, end: CareersApplicationEnd): string => {
	const summary = t("careers.end.ended_at", { step: end.step_name, summary: endSummaryText(t, end) });
	return end.note === null ? summary : t("careers.end.with_note", { summary, note: end.note });
};

/** "Tue 6 Oct, 14:00" — the weekday for the glance, the date so next week's Tuesday isn't read as this one's. */
const INTERVIEW_AT_FORMAT = "ccc d LLL, HH:mm";

/** An interview time as the department's clock reads it — never the viewer's own zone. */
export const formatInterviewAt = (at: DateTime, timeZone: string): string => at.setZone(timeZone).toFormat(INTERVIEW_AT_FORMAT);

/** A past interview still shows, muted, until someone changes it: it is a fact that needs a follow-up. */
export const isInterviewPast = (at: DateTime, now: DateTime): boolean => at.toMillis() < now.toMillis();

/**
 * The instant a picked date + time mean ON THE DEPARTMENT'S CLOCK: the calendar day from `date`, the hour and
 * minute from `time`, in `timeZone` — whatever zones the two pickers' own values carry. A wall time the zone
 * skips (a spring-forward gap) comes out an hour later, as Luxon resolves it.
 */
export const interviewAtFrom = (date: DateTime, time: DateTime, timeZone: string): DateTime => {
	const at = DateTime.fromObject({ year: date.year, month: date.month, day: date.day, hour: time.hour, minute: time.minute }, { zone: timeZone });
	if (!at.isValid) throw new Error(`Invalid interview time in ${timeZone}: ${at.invalidExplanation}`);
	return at;
};

/** The Mismatches column: nothing at 0 (the cell shows "—"), otherwise "N answer(s)" off i18next plurals. */
export const flagCountLabel = (mismatchCount: number): TranslatableLabel | null =>
	mismatchCount > 0 ? { key: "careers.flags.answers", params: { count: mismatchCount } } : null;

/** The table's Notes cell: "2 notes" / "1 note" / "No notes" — a count read as what it counts, never a bare "0". */
export const noteCountLabel = (noteCount: number): TranslatableLabel =>
	noteCount === 0 ? { key: "careers.table.notes_none", params: {} } : { key: "careers.notes.count", params: { count: noteCount } };

/** "6 years" / "4.5 years"; null when unknown, so a subtitle can skip the part instead of printing "—". */
export const yearsLabel = (yearsExperience: number | null): TranslatableLabel | null =>
	yearsExperience === null ? null : { key: "careers.years", params: { count: yearsExperience } };

export type StepFilterOption = { value: string; label: string };

/**
 * The table's "Any step" options. With a job chosen, that job's steps in order. With "All jobs", every
 * distinct step NAME across jobs — ids differ per job, but "Interview" in two jobs is one thing to a
 * reader — in the board's lane order (`boardColumns`), spelled as it first appears.
 */
export const stepFilterOptions = (jobs: Pick<CareersJob, "id" | "steps">[], jobId: string | null): StepFilterOption[] =>
	boardColumns(jobId === null ? jobs : jobs.filter((job) => job.id === jobId)).map((column) => ({ value: stepNameKey(column.name), label: column.name }));

/**
 * The table's outcome / reason filter: an outcome alone (`reason` null: "every Withdrew"), one of its reasons, or
 * only its ends without one (`not_given`). Each reaches ended applications only.
 */
export type EndFilter = { outcome: CareersEndOutcome; reason: CareersEndReason | CareersEndReasonNotGiven | null };

export type ApplicationFilters = {
	/**
	 * The searches (kept ones and what is being typed): each must be in the candidate's name, email, license, or a
	 * job title or workplace in their history. None keeps everyone.
	 */
	terms: string[];
	/** A `stepFilterOptions` value, or null for any step. */
	stepKey: string | null;
	status: CareersApplicationStatus | null;
	end: EndFilter | null;
};

const matchesSearch = ({ candidate }: CareersApplicationRow, needle: string): boolean =>
	[candidate.full_name, candidate.email, candidate.license_number, candidate.license_type].some((field) => field?.toLocaleLowerCase().includes(needle)) ||
	matchingWorkHistoryEntry(candidate.work_history, needle) !== undefined;

const matchesEnd = ({ end }: CareersApplicationRow, filter: EndFilter): boolean =>
	end !== null && end.outcome === filter.outcome && (filter.reason === null || end.reason === filter.reason);

/** The table's client-side filter. The list endpoint already scopes to the department and the job. */
export const filterApplications = (rows: CareersApplicationRow[], filters: ApplicationFilters): CareersApplicationRow[] => {
	const needles = searchNeedles(filters.terms, "");
	return rows.filter(
		(row) =>
			needles.every((needle) => matchesSearch(row, needle)) &&
			(filters.stepKey === null || stepNameKey(row.current_step_name) === filters.stepKey) &&
			(filters.status === null || row.status === filters.status) &&
			(filters.end === null || matchesEnd(row, filters.end)),
	);
};

/**
 * The board's filter: people for whom every search is in their name or in a job title or workplace in their
 * history, any case — "meta" and "lena" finds Lena who worked at Meta. No searches keeps everyone.
 */
export const filterByCandidateNameOrJob = (rows: CareersApplicationRow[], terms: string[]): CareersApplicationRow[] => {
	const needles = searchNeedles(terms, "");
	return rows.filter(({ candidate }) =>
		needles.every(
			(needle) => candidate.full_name.toLocaleLowerCase().includes(needle) || matchingWorkHistoryEntry(candidate.work_history, needle) !== undefined,
		),
	);
};

/**
 * Why a card matched the board's filter when it wasn't only their name: the job in their history the first search
 * not in their name found. Null with no searches, or when their name holds every one — the card then needs no
 * explanation.
 */
export const workHistoryMatchFor = (candidate: Pick<CareersCandidate, "full_name" | "work_history">, terms: string[]): CareersWorkHistoryEntry | null => {
	const notInName = searchNeedles(terms, "").find((needle) => !candidate.full_name.toLocaleLowerCase().includes(needle));
	return notInName === undefined ? null : (matchingWorkHistoryEntry(candidate.work_history, notInName) ?? null);
};

/**
 * The Jobs tab's search: jobs whose title or team holds every search, any case — the two things the Role cell
 * shows. "nurse" and "night" keeps a Night shift Charge Nurse. No searches keeps every job.
 */
export const filterJobs = <J extends Pick<CareersJob, "title" | "team">>(jobs: J[], terms: string[]): J[] => {
	const needles = searchNeedles(terms, "");
	return jobs.filter(({ title, team }) => needles.every((needle) => [title, team].some((field) => field?.toLocaleLowerCase().includes(needle))));
};

/** "Maria Alvarez" → "MA"; one word → its first letter. */
export const initialsOf = (fullName: string): string =>
	fullName
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.map((part) => part.charAt(0).toLocaleUpperCase())
		.filter((_, index, parts) => index === 0 || index === parts.length - 1)
		.join("");

/**
 * Free text the backend stored (an answer, a note) as it was typed.
 *
 * `axiosInstance` turns ANY response string that is exactly an ISO date into a Luxon `DateTime` — so a
 * screening answer of "2027-03-01" (a license expiry, say) would arrive as an object and crash the render.
 * This restores the text; keep it at the query boundary for user- and model-authored fields.
 */
export const restoreText = (value: unknown): string => {
	if (typeof value === "string") return value;
	if (DateTime.isDateTime(value)) {
		// A date-only string round-trips through `toISODate`; a full timestamp through `toISO`.
		const date = value.toISODate();
		const hasTime = value.hour !== 0 || value.minute !== 0 || value.second !== 0 || value.millisecond !== 0;
		const text = hasTime ? value.toISO() : date;
		if (text !== null) return text;
	}
	throw new Error(`Expected text from the careers API, got ${typeof value}`);
};

/** `restoreText` for a nullable field. */
export const restoreNullableText = (value: unknown): string | null => (value === null ? null : restoreText(value));

/** Trim a form field; an empty one is sent as null (the API rejects "" with a 422). */
export const toNullableText = (value: string): string | null => {
	const trimmed = value.trim();
	return trimmed === "" ? null : trimmed;
};

/**
 * A pipeline step's categorical colour, by its index in the job's ordered steps. Keyed by position, not
 * by name, so the Nth step wears the same colour in every job. The hired step keeps its position's colour
 * too — its mark is a glyph, not a hue, so no colour ever means "hired" on one job and not on another.
 * The palette's automatic order (any six read apart, none passes for a status colour), cycling past its end.
 * The ink shade: it is the chip's text, on a tint of itself.
 */
export const stepAccentColor = (index: number): string => categoricalColorAt(index).ink;

/**
 * A job's categorical colour (the palette's "one colour per role/job/department"), keyed off the job id
 * rather than its list position — so closing a job, which reorders the list, never repaints the others.
 * Two jobs can share a colour; the dot always sits beside the job's name, which tells them apart.
 */
export const jobAccentColor = (jobId: string): string => freeLabelColor(jobId).dot;
